import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { hasPermission } from '../common/permissions';

const emptyForm = {
  plan_name: '',
  scope_type: 'Process-only',
  critical_process: '',
  dependencies: '',
  department_id: '',
  risk_id: '',
  she_event_id: '',
  rto_hours: 24,
  rpo_hours: 24,
  plan_status: 'Draft',
  owner_id: '',
  readiness_score: 0,
  scenario_test_notes: '',
  last_tested: '',
  next_test_date: ''
};

const BusinessContinuity = ({ user }) => {
  const [plans, setPlans] = useState([]);
  const [metadata, setMetadata] = useState({ risks: [], sheEvents: [], departments: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

  const canAdd = hasPermission(user, 'Business Continuity', 'add');
  const canEdit = hasPermission(user, 'Business Continuity', 'edit');
  const canDelete = hasPermission(user, 'Business Continuity', 'delete');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, metaRes] = await Promise.all([
        api.get('/bcm-plans'),
        api.get('/bcm-metadata')
      ]);
      setPlans(plansRes.data || []);
      setMetadata(metaRes.data || { risks: [], sheEvents: [], departments: [], users: [] });
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to load continuity plans.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const departmentName = (id) => metadata.departments?.find(d => d.id === id)?.name || id || 'Unassigned';
  const ownerName = (plan) => {
    if (plan.owner) return `${plan.owner.first_name} ${plan.owner.last_name}`;
    const found = metadata.users?.find(u => u.user_id === plan.owner_id);
    return found ? `${found.first_name} ${found.last_name}` : 'Unassigned';
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return plans.filter(plan =>
      plan.plan_reference?.toLowerCase().includes(term) ||
      plan.plan_name?.toLowerCase().includes(term) ||
      plan.critical_process?.toLowerCase().includes(term) ||
      departmentName(plan.department_id)?.toLowerCase().includes(term)
    );
  }, [plans, search, metadata.departments]);

  const stats = useMemo(() => {
    const avg = plans.length ? plans.reduce((sum, p) => sum + Number(p.readiness_score || 0), 0) / plans.length : 0;
    const due = plans.filter(p => p.next_test_date && new Date(p.next_test_date) <= new Date()).length;
    const approved = plans.filter(p => ['Approved', 'Active'].includes(p.plan_status)).length;
    return { avg: Math.round(avg), due, approved };
  }, [plans]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFieldErrors({});
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      plan_name: plan.plan_name || '',
      scope_type: plan.scope_type || 'Process-only',
      critical_process: plan.critical_process || '',
      dependencies: plan.dependencies || '',
      department_id: plan.department_id || '',
      risk_id: plan.risk_id || '',
      she_event_id: plan.she_event_id || '',
      rto_hours: plan.rto_hours || 24,
      rpo_hours: plan.rpo_hours || 24,
      plan_status: plan.plan_status || 'Draft',
      owner_id: plan.owner_id || '',
      readiness_score: plan.readiness_score || 0,
      scenario_test_notes: plan.scenario_test_notes || '',
      last_tested: plan.last_tested ? plan.last_tested.slice(0, 10) : '',
      next_test_date: plan.next_test_date ? plan.next_test_date.slice(0, 10) : ''
    });
    setFieldErrors({});
    setDialogOpen(true);
  };

  const save = async () => {
    if (saving) return;

    const validationErrors = validateForm();
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        risk_id: form.risk_id || null,
        she_event_id: form.she_event_id || null,
        owner_id: form.owner_id || null,
        department_id: form.department_id || null,
        readiness_score: Number(form.readiness_score || 0),
        rto_hours: Number(form.rto_hours || 0),
        rpo_hours: Number(form.rpo_hours || 0)
      };
      const response = editing
        ? await api.put(`/bcm-plans/${editing.plan_id}`, payload)
        : await api.post('/bcm-plans', payload);
      const savedPlan = response.data?.plan;
      if (savedPlan) {
        setPlans(current => editing
          ? current.map(item => item.plan_id === savedPlan.plan_id ? savedPlan : item)
          : [savedPlan, ...current]
        );
      }
      setNotice({ open: true, message: editing ? 'Continuity plan updated.' : 'Continuity plan recorded.', severity: 'success' });
      setFieldErrors({});
      setDialogOpen(false);
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to save continuity plan.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (plan) => {
    if (!window.confirm(`Delete ${plan.plan_reference}?`)) return;
    try {
      await api.delete(`/bcm-plans/${plan.plan_id}`);
      setNotice({ open: true, message: 'Continuity plan deleted.', severity: 'success' });
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to delete continuity plan.', severity: 'error' });
    }
  };

  const readinessColor = (value) => Number(value) >= 75 ? '#10b981' : Number(value) >= 50 ? '#f59e0b' : '#ef4444';
  const scopeOptions = ['Specific Risk', 'Specific SHE Event', 'Department-wide', 'Enterprise-wide', 'Process-only'];

  const validateForm = () => {
    const errors = {};
    if (!form.plan_name.trim()) errors.plan_name = 'Plan Name is required.';
    if (!form.critical_process.trim()) errors.critical_process = 'Critical Process is required.';
    if (!form.scope_type) errors.scope_type = 'Plan Scope is required.';
    if (form.scope_type === 'Specific Risk' && !form.risk_id) errors.risk_id = 'Linked Risk is required for Specific Risk scope.';
    if (form.scope_type === 'Specific SHE Event' && !form.she_event_id) errors.she_event_id = 'Linked SHE Event is required for Specific SHE Event scope.';
    if (form.scope_type === 'Department-wide' && !form.department_id) errors.department_id = 'Department is required for Department-wide plans.';

    const rto = Number(form.rto_hours);
    const rpo = Number(form.rpo_hours);
    const readiness = Number(form.readiness_score);
    if (!Number.isFinite(rto) || rto < 0) errors.rto_hours = 'RTO Hours must be zero or greater.';
    if (!Number.isFinite(rpo) || rpo < 0) errors.rpo_hours = 'RPO Hours must be zero or greater.';
    if (!Number.isFinite(readiness) || readiness < 0 || readiness > 100) errors.readiness_score = 'Readiness must be between 0 and 100.';
    if (form.last_tested && form.next_test_date && form.next_test_date < form.last_tested) errors.next_test_date = 'Next Test Date cannot be before Last Tested.';
    return errors;
  };

  const updateField = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(current => ({ ...current, [field]: undefined }));
    }
  };

  const fieldProps = (field) => ({
    error: Boolean(fieldErrors[field]),
    helperText: fieldErrors[field] || ' ',
    FormHelperTextProps: {
      sx: {
        color: '#fca5a5',
        fontWeight: 'bold',
        ml: 0.5
      }
    }
  });

  const scrollToFirstError = (errors) => {
    const fieldOrder = [
      'plan_name',
      'critical_process',
      'scope_type',
      'department_id',
      'risk_id',
      'she_event_id',
      'rto_hours',
      'rpo_hours',
      'readiness_score',
      'next_test_date'
    ];
    const firstInvalidField = fieldOrder.find(field => errors[field]);

    window.setTimeout(() => {
      const target = document.querySelector(`[data-bcm-field="${firstInvalidField}"]`);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = target.querySelector('input, textarea, [role="combobox"]');
      if (input) input.focus();
    }, 80);
  };

  const triggerLabel = (plan) => {
    if (plan.scope_type === 'Specific Risk') {
      return plan.risk?.sn || 'Risk not found';
    }
    if (plan.scope_type === 'Specific SHE Event') {
      return plan.she_event?.action_id || 'SHE event not found';
    }
    if (plan.scope_type === 'Department-wide') {
      return departmentName(plan.department_id);
    }
    if (plan.scope_type === 'Enterprise-wide') {
      return 'Enterprise-wide';
    }
    return plan.critical_process || 'Process-only';
  };

  const fieldSx = {
    '& .MuiInputBase-root': {
      color: '#f8fafc',
      background: 'rgba(15, 23, 42, 0.45)'
    },
    '& .MuiInputLabel-root': {
      color: '#cbd5e1'
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#f8fafc'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(203, 213, 225, 0.28)'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(248, 250, 252, 0.45)'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981'
    },
    '& .MuiSelect-icon, & input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)',
      color: '#f8fafc'
    }
  };

  const selectMenuProps = {
    MenuProps: {
      PaperProps: {
        sx: {
          bgcolor: '#1e293b',
          color: '#f8fafc',
          border: '1px solid rgba(255,255,255,0.12)'
        }
      }
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#10b981' }} /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>Business Continuity</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField size="small" placeholder="Search plans..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { color: 'white', background: 'rgba(255,255,255,0.04)' } }} />
          {canAdd && <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 'bold', bgcolor: '#10b981' }}>Add BCM Plan</Button>}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Average Readiness', `${stats.avg}%`, '#10b981'],
          ['Plans Due for Test', stats.due, '#f59e0b'],
          ['Approved / Active', stats.approved, '#3b82f6']
        ].map(([label, value, color]) => (
          <Grid item xs={12} md={4} key={label}>
            <Paper className="glass" sx={{ p: 2.5, borderRadius: '16px', background: 'rgba(30,41,59,0.45)' }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>{label}</Typography>
              <Typography sx={{ color, fontSize: '1.5rem', fontWeight: 'bold' }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <TableContainer component={Paper} sx={{ background: 'rgba(15,23,42,0.75)', borderRadius: '18px', border: '1px solid rgba(255,255,255,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { color: '#94a3b8', fontWeight: 'bold' } }}>
              <TableCell>Reference</TableCell>
              <TableCell>Critical Process</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>RTO / RPO</TableCell>
              <TableCell>Readiness</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(plan => (
              <TableRow key={plan.plan_id} sx={{ '& td': { color: '#f1f5f9', borderColor: 'rgba(255,255,255,0.05)' } }}>
                <TableCell sx={{ color: '#10b981 !important', fontWeight: 'bold' }}>{plan.plan_reference}</TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{plan.critical_process}</Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>{plan.plan_name}</Typography>
              </TableCell>
              <TableCell>
                <Chip size="small" label={plan.scope_type || 'Process-only'} sx={{ color: '#cbd5e1', bgcolor: 'rgba(255,255,255,0.05)', fontWeight: 'bold', mb: 0.5 }} />
                <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>{triggerLabel(plan)}</Typography>
              </TableCell>
              <TableCell>{departmentName(plan.department_id)}</TableCell>
                <TableCell>{plan.rto_hours}h / {plan.rpo_hours}h</TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress variant="determinate" value={Number(plan.readiness_score || 0)} sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)', '& .MuiLinearProgress-bar': { bgcolor: readinessColor(plan.readiness_score) } }} />
                    <Typography sx={{ color: readinessColor(plan.readiness_score), fontWeight: 'bold', fontSize: '0.8rem' }}>{plan.readiness_score}%</Typography>
                  </Box>
                </TableCell>
                <TableCell><Chip size="small" label={plan.plan_status} sx={{ color: plan.plan_status === 'Active' ? '#10b981' : '#f59e0b', bgcolor: 'rgba(255,255,255,0.05)', fontWeight: 'bold' }} /></TableCell>
                <TableCell>{ownerName(plan)}</TableCell>
                <TableCell align="right">
                  {canEdit && <Button size="small" onClick={() => openEdit(plan)} sx={{ color: '#3b82f6', textTransform: 'none' }}>Edit</Button>}
                  {canDelete && <Button size="small" startIcon={<DeleteIcon />} onClick={() => remove(plan)} sx={{ color: '#ef4444', textTransform: 'none' }}>Delete</Button>}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ color: '#64748b', py: 6 }}>No continuity plans found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{editing ? `Edit ${editing.plan_reference}` : 'Create Continuity Plan'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6} data-bcm-field="plan_name"><TextField fullWidth required label="Plan Name" value={form.plan_name} onChange={(e) => updateField('plan_name', e.target.value)} sx={fieldSx} {...fieldProps('plan_name')} /></Grid>
            <Grid item xs={12} md={6} data-bcm-field="critical_process"><TextField fullWidth required label="Critical Process" value={form.critical_process} onChange={(e) => updateField('critical_process', e.target.value)} sx={fieldSx} {...fieldProps('critical_process')} /></Grid>
            <Grid item xs={12} md={4} data-bcm-field="scope_type">
              <TextField select required fullWidth label="Plan Scope" value={form.scope_type} onChange={(e) => {
                setForm({ ...form, scope_type: e.target.value, risk_id: '', she_event_id: '', department_id: e.target.value === 'Enterprise-wide' ? '' : form.department_id });
                setFieldErrors(current => ({ ...current, scope_type: undefined, risk_id: undefined, she_event_id: undefined, department_id: undefined }));
              }} sx={fieldSx} SelectProps={selectMenuProps} {...fieldProps('scope_type')}>
                {scopeOptions.map(scope => <MenuItem key={scope} value={scope}>{scope}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4} data-bcm-field="department_id"><TextField select required={form.scope_type === 'Department-wide'} fullWidth label="Department" value={form.department_id} onChange={(e) => updateField('department_id', e.target.value)} sx={fieldSx} SelectProps={selectMenuProps} disabled={form.scope_type === 'Enterprise-wide'} {...fieldProps('department_id')}>{metadata.departments?.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}</TextField></Grid>
            {form.scope_type === 'Specific Risk' && (
              <Grid item xs={12} md={4} data-bcm-field="risk_id">
                <TextField select required fullWidth label="Linked Risk" value={form.risk_id} onChange={(e) => updateField('risk_id', e.target.value)} sx={fieldSx} SelectProps={selectMenuProps} {...fieldProps('risk_id')}>
                <MenuItem value="">None</MenuItem>
                {metadata.risks?.map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.sn} - {r.risk_description?.length > 70 ? `${r.risk_description.slice(0, 70)}...` : r.risk_description}
                  </MenuItem>
                ))}
                </TextField>
              </Grid>
            )}
            {form.scope_type === 'Specific SHE Event' && (
              <Grid item xs={12} md={4} data-bcm-field="she_event_id">
                <TextField select required fullWidth label="Linked SHE Event" value={form.she_event_id} onChange={(e) => updateField('she_event_id', e.target.value)} sx={fieldSx} SelectProps={selectMenuProps} {...fieldProps('she_event_id')}>
                  <MenuItem value="">None</MenuItem>
                  {metadata.sheEvents?.map(s => {
                    const sheDescription = s.description || s.activity_category || 'SHE record';
                    return (
                      <MenuItem key={s.id} value={s.id}>
                        {s.action_id} - {s.activity_category} - {sheDescription.length > 55 ? `${sheDescription.slice(0, 55)}...` : sheDescription}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} md={4}><TextField select fullWidth label="Owner" value={form.owner_id} onChange={(e) => updateField('owner_id', e.target.value)} sx={fieldSx} SelectProps={selectMenuProps} helperText=" "><MenuItem value="">Current User</MenuItem>{metadata.users?.map(u => <MenuItem key={u.user_id} value={u.user_id}>{u.first_name} {u.last_name}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={3} data-bcm-field="rto_hours"><TextField fullWidth required type="number" label="RTO Hours" value={form.rto_hours} inputProps={{ min: 0 }} onChange={(e) => updateField('rto_hours', e.target.value)} sx={fieldSx} {...fieldProps('rto_hours')} /></Grid>
            <Grid item xs={12} md={3} data-bcm-field="rpo_hours"><TextField fullWidth required type="number" label="RPO Hours" value={form.rpo_hours} inputProps={{ min: 0 }} onChange={(e) => updateField('rpo_hours', e.target.value)} sx={fieldSx} {...fieldProps('rpo_hours')} /></Grid>
            <Grid item xs={12} md={3} data-bcm-field="readiness_score"><TextField fullWidth required type="number" label="Readiness %" value={form.readiness_score} inputProps={{ min: 0, max: 100 }} onChange={(e) => updateField('readiness_score', e.target.value)} sx={fieldSx} {...fieldProps('readiness_score')} /></Grid>
            <Grid item xs={12} md={3}><TextField select fullWidth label="Status" value={form.plan_status} onChange={(e) => updateField('plan_status', e.target.value)} sx={fieldSx} SelectProps={selectMenuProps} helperText=" ">{['Draft', 'Under Review', 'Approved', 'Active'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}</TextField></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth type="date" label="Last Tested" InputLabelProps={{ shrink: true }} value={form.last_tested} onChange={(e) => updateField('last_tested', e.target.value)} sx={fieldSx} helperText=" " /></Grid>
            <Grid item xs={12} md={6} data-bcm-field="next_test_date"><TextField fullWidth type="date" label="Next Test Date" InputLabelProps={{ shrink: true }} value={form.next_test_date} onChange={(e) => updateField('next_test_date', e.target.value)} sx={fieldSx} {...fieldProps('next_test_date')} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Dependencies" value={form.dependencies} onChange={(e) => updateField('dependencies', e.target.value)} sx={fieldSx} helperText=" " /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Scenario Test Notes" value={form.scenario_test_notes} onChange={(e) => updateField('scenario_test_notes', e.target.value)} sx={fieldSx} helperText=" " /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving} sx={{ bgcolor: '#10b981', textTransform: 'none', fontWeight: 'bold', '&.Mui-disabled': { bgcolor: 'rgba(16,185,129,0.45)', color: '#d1fae5' } }}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={saving} sx={{ color: '#fff', zIndex: 2000, background: 'rgba(15, 23, 42, 0.62)', backdropFilter: 'blur(2px)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderRadius: '14px', background: '#1e293b', border: '1px solid rgba(255,255,255,0.12)' }}>
          <CircularProgress size={24} sx={{ color: '#10b981' }} />
          <Typography sx={{ color: '#f8fafc', fontWeight: 'bold' }}>Saving BCM plan...</Typography>
        </Box>
      </Backdrop>

      <Snackbar open={notice.open} autoHideDuration={4000} onClose={() => setNotice({ ...notice, open: false })}>
        <Alert severity={notice.severity} variant="filled">{notice.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessContinuity;
